import { useScreenDetect } from '@sgcmpteltd/uikit-next';
import {
  BellContained,
  BellOutlined,
  DistributorShop,
  IosArrowDown,
  IosArrowHorizontalScroller,
  IosArrowUp,
} from '@sgcmpteltd/uikit-react';
import {
  MENU_BORDER_RADIUS_EXTRA_LARGE,
  MENU_LAYER_POSITION_FIXED,
  Select,
} from '@sgcmpteltd/uikit-react/components/select';
import cx from 'clsx';
import Cookies from 'js-cookie';
import { useState } from 'react';

import type {
  CamelCaseAdAlert,
  CamelCaseCarAlert,
} from '@/apis/server/new-car/v1/my-alert';
import type { User } from '@/app/types';
import { ImageWithFallback } from '@/components/image-with-fallback';
import {
  COOKIE_KEY_MAIL,
  COOKIE_KEY_SESSION_ID,
  COOKIE_KEY_USER_ID,
} from '@/configs/cookies';
import {
  joinSgcmMedia,
  joinSgcmMobileSite,
  joinSgcmSite,
} from '@/utils/url/join/public';

import { AlertModal } from '../alert-modal';
import { NewCarInfoList as BaseNewCarInfoList } from '../info-list';
import { PriceListModal } from '../pricelist-model';
import { TermsAndConditionsModal } from '../terms-and-conditions-modal';
import {
  AD_FEATURE_TABS_SCROLL_END_TOLERANCE,
  COLLAPSIBLE_ARRAOW_ICON_SIZE,
  ICON_SIZE,
  ICON_SIZE_IN_PIXEL,
} from './constants';
import { AdInfoItem } from './item';
import styles from './styles.module.scss';
import type { Component } from './types';

export const AdInfoList: Component = ({
  adAlerts,
  adInfoList,
  adMenuOptions,
  onAlertModalSubmit,
}) => {
  const { isDesktop } = useScreenDetect();
  const isDesktopMode = isDesktop();
  const user: User = {
    email: Cookies.get(COOKIE_KEY_MAIL) || '',
    userId: Number(Cookies.get(COOKIE_KEY_USER_ID)) || 0,
    sessionId: Cookies.get(COOKIE_KEY_SESSION_ID) || '',
  };
  const {
    id,
    name: adName,
    serviceCentreId,
    brand,
    serviceCentres,
    pricelistDates,
    websiteUrl,
    termsConditions,
  } = adInfoList;
  const numOfServiceCentres = serviceCentres.length;
  const [
    isContentOpen,
    setIsContentOpen,
  ] = useState(false);
  const [
    isPriceListModalOpen,
    setIsPriceListModalOpen,
  ] = useState(false);
  const [
    isTermsModalOpen,
    setIsTermsModalOpen,
  ] = useState(false);
  const [
    isAlertModalOpen,
    setIsAlertModalOpen,
  ] = useState(false);

  const handlePriceListClick = () => {
    setIsPriceListModalOpen(true);
  };

  const handleTermsClick = () => {
    setIsTermsModalOpen(true);
  };

  const handleAlertClick = () => {
    if (user.sessionId) {
      setIsAlertModalOpen(true);
    } else {
      window.open(
        `${joinSgcmSite('/member/login')}`,
        '_self',
      );
    }
  };

  const handleCollapsibleClick = () => {
    setIsContentOpen(!isContentOpen);
  };

  const handleSelectClick = (dealerId: string | number) => {
    const itemUrl = new URL(window.location.href);

    itemUrl.searchParams.set('dealer_id', `${dealerId}`);
    const selectedBrand = adMenuOptions.find(
      option => option.dealerId === dealerId,
    )?.brand;

    itemUrl.searchParams.set('q', `${selectedBrand}`);
    itemUrl.searchParams.set('category', 'ad');
    window.open(itemUrl, '_self');
  };

  const handleAlertModalSubmit = (
    alerts: CamelCaseAdAlert | CamelCaseCarAlert,
  ) => {
    onAlertModalSubmit?.(alerts);
  };

  const adAlertModalContent = {
    adInfo: {
      id,
      name: adName,
      brand,
    },
    adAlert: adAlerts,
  };

  const CollapsibleArrowIcon = isContentOpen ? IosArrowUp : IosArrowDown;

  return (
    <div className={styles.containerRoot}>
      <PriceListModal
        dealerId={id}
        isOpen={isPriceListModalOpen}
        priceList={pricelistDates}
        onClose={() => {
          setIsPriceListModalOpen(false);
        }} />
      <TermsAndConditionsModal
        isOpen={isTermsModalOpen}
        termsContentList={termsConditions}
        onClose={() => {
          setIsTermsModalOpen(false);
        }} />
      <AlertModal
        adAlertModalContent={adAlertModalContent}
        isOpen={isAlertModalOpen}
        type="adAlert"
        user={user}
        onClose={() => {
          setIsAlertModalOpen(false);
        }}
        onSubmit={handleAlertModalSubmit} />
      <div className={styles.containerHeaderRow}>
        <div className={styles.titleContainer}>
          <div className={styles.textAdName}>
            {adName}
          </div>
          <div className={styles.containerBrandMenu}>
            <Select
              headerClassName={cx(
                styles.containerSelectHeader,
              )}
              menuBorderRadius={MENU_BORDER_RADIUS_EXTRA_LARGE}
              menuContainerClassName={cx(
                styles.containerSelectMenu,
              )}
              menuLayerPosition={MENU_LAYER_POSITION_FIXED}
              optionList={
                adMenuOptions.map(({
                  dealerId: value,
                  dealerName: text,
                }) => ({
                  value,
                  text,
                }))
              }
              placeholder={{
                text: 'Select Authorised Distributor',
              }}
              value=""
              onChange={handleSelectClick} />
          </div>
        </div>
        <div className={styles.containerScollFeatures}>
          <IosArrowHorizontalScroller
            arrowLeftContainerClassName={cx(
              styles.containerNavBarArrow,
              styles.isLeft,
            )}
            arrowRightContainerClassName={cx(
              styles.containerNavBarArrow,
              styles.isRight,
            )}
            scrollDistance={({ clientWidth }) => clientWidth}
            scrollEndTolerance={AD_FEATURE_TABS_SCROLL_END_TOLERANCE}>
            <ul className={cx(
              styles.containerAdFeatures,
            )}>
              <div className={styles.textAdFeature}>
                <a
                  className={styles.textAdFeature}
                  href={isDesktopMode
                    ? `${joinSgcmSite(`/directory/merchant.php?MID=${serviceCentreId}`)}`
                    : `${joinSgcmMobileSite(`/directory/merchant.php?MID=${serviceCentreId}`)}`}
                  rel="noreferrer">
                  <DistributorShop
                    className={styles.buttonAdFeature}
                    size={ICON_SIZE} />
                  <span>
                    Service Centres
                  </span>
                </a>
              </div>
              <div className={styles.textAdFeature}>
                <a
                  className={styles.textAdFeature}
                  href={isDesktopMode
                    ? `${joinSgcmSite(`new_cars/newcars_promotionslist.php?Brand=${brand}`)}`
                    : `${joinSgcmMobileSite(`new_cars/promotionlist.php?Brand=${brand}`)}`}
                  rel="noreferrer">
                  <ImageWithFallback
                    alt="promotion icon"
                    className={styles.buttonAdFeature}
                    height={ICON_SIZE_IN_PIXEL}
                    src={`${joinSgcmMedia('/public/new-cars/icon/outlinedPromotion.svg')}`}
                    title="promotion"
                    width={ICON_SIZE_IN_PIXEL}
                    priority />
                  <span>
                    Promotion
                  </span>
                </a>
              </div>
              {pricelistDates.length > 0
              && (
                <div className={styles.textAdFeature}>
                  <button
                    className={styles.containerModal}
                    onClick={handlePriceListClick}>
                    <ImageWithFallback
                      alt="pricelist icon"
                      className={styles.buttonAdFeature}
                      height={ICON_SIZE_IN_PIXEL}
                      src={`${joinSgcmMedia('/public/new-cars/icon/pricelist.svg')}`}
                      title="pricelist"
                      width={ICON_SIZE_IN_PIXEL}
                      priority />
                    <span>
                      Pricelist
                    </span>
                  </button>
                </div>
              )}
              {websiteUrl
              && (
                <div className={styles.textAdFeature}>
                  <a
                    className={styles.textAdFeature}
                    href={`https://${websiteUrl}`}
                    rel="noreferrer"
                    target="_blank">
                    <ImageWithFallback
                      alt="website icon"
                      className={styles.buttonAdFeature}
                      height={ICON_SIZE_IN_PIXEL}
                      src={`${joinSgcmMedia('/public/new-cars/icon/outlinedWebsite.svg')}`}
                      title="websit"
                      width={ICON_SIZE_IN_PIXEL}
                      priority />
                    <span>
                      {websiteUrl}
                    </span>
                  </a>
                </div>
              )}
              {termsConditions.length !== 0
              && (
                <div className={styles.textAdFeature}>
                  <button
                    className={styles.containerModal}
                    onClick={handleTermsClick}>
                    <ImageWithFallback
                      alt="terms and condition icon"
                      className={styles.buttonAdFeature}
                      height={ICON_SIZE_IN_PIXEL}
                      src={`${joinSgcmMedia('/public/new-cars/icon/outlinedDocument.svg')}`}
                      title="termsAndCondition"
                      width={ICON_SIZE_IN_PIXEL}
                      priority />
                    <span>
                      Terms & Conditions
                    </span>
                  </button>
                </div>
              )}
              <div className={styles.textAdFeature}>
                <button
                  className={styles.containerModal}
                  onClick={handleAlertClick}>
                  {adAlerts && (adAlerts.adPromo || adAlerts.adCarLaunches)
                    ? (
                      <BellContained
                        className={styles.buttonAdFeature}
                        size={ICON_SIZE} />
                    )
                    : (
                      <BellOutlined
                        className={styles.buttonAdFeature}
                        size={ICON_SIZE} />
                    )}
                  <span>
                    Alert me
                  </span>
                </button>
              </div>
            </ul>
          </IosArrowHorizontalScroller>
        </div>
      </div>
      <div
        className={cx(
          styles.collapsibleContainerRoot,
          numOfServiceCentres === 2 && styles.isDouble,
          isContentOpen ? styles.isExpanded : styles.isCollapsed,
        )}>
        <BaseNewCarInfoList>
          {
            serviceCentres.map(({
              name,
              hotlines,
              address,
              openingHours,
              salesContacts,
            }) => {
              const adInfo = {
                name,
                hotlines,
                address,
                openingHours,
                salesContacts,
              };
              return (
                <AdInfoItem
                  key={name}
                  adInfo={adInfo} />
              );
            })
          }
        </BaseNewCarInfoList>
      </div>
      {!isDesktop() && numOfServiceCentres >= 3 && (
        <button
          className={cx(
            styles.containerCollapsibleButton,
            isContentOpen && styles.isOpen,
          )}
          onClick={handleCollapsibleClick}>
          <span className={styles.textCollapsible}>
            {
              isContentOpen ? 'Show less' : 'Show more'
            }
          </span>
          <CollapsibleArrowIcon size={COLLAPSIBLE_ARRAOW_ICON_SIZE} />
        </button>
      )}
    </div>
  );
};
